import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TextCleanerTool = () => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState({
    trimLines: true,
    removeExtraSpaces: true,
    removeEmptyLines: true,
    removeLineBreaks: false,
    removeSpecialChars: false,
    removeNumbers: false,
  });

  const cleanText = () => {
    if (!input) return '';
    let result = input;

    if (options.trimLines) {
      result = result.split('\n').map(line => line.trim()).join('\n');
    }
    if (options.removeExtraSpaces) {
      result = result.replace(/  +/g, ' ');
    }
    if (options.removeEmptyLines) {
      result = result.replace(/\n\s*\n/g, '\n');
    }
    if (options.removeLineBreaks) {
      result = result.replace(/\n/g, ' ').replace(/  +/g, ' ');
    }
    if (options.removeSpecialChars) {
      result = result.replace(/[^a-zA-Z0-9\s\n]/g, '');
    }
    if (options.removeNumbers) {
      result = result.replace(/\d/g, '');
    }

    return result.trim();
  };

  const output = cleanText();

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success('Cleaned text copied!');
  };

  const toggleOption = (key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6" data-testid="text-cleaner-tool">
      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Cleaning Options
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { key: 'trimLines', label: 'Trim Line Whitespace' },
            { key: 'removeExtraSpaces', label: 'Remove Extra Spaces' },
            { key: 'removeEmptyLines', label: 'Remove Empty Lines' },
            { key: 'removeLineBreaks', label: 'Remove Line Breaks' },
            { key: 'removeSpecialChars', label: 'Remove Special Characters' },
            { key: 'removeNumbers', label: 'Remove Numbers' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={options[key]}
                onChange={() => toggleOption(key)}
                className="rounded"
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">
            Input Text ({input.length} chars)
          </label>
          <Textarea
            placeholder="Paste messy text here to clean..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="min-h-[250px] font-mono text-sm bg-muted/50"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">
            Cleaned Output ({output.length} chars)
          </label>
          <Textarea
            value={output}
            readOnly
            className="min-h-[250px] font-mono text-sm bg-muted/50"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyOutput} disabled={!output}>
          <Copy className="w-4 h-4 mr-2" /> Copy Cleaned Text
        </Button>
        <Button onClick={() => setInput(output)} variant="outline" disabled={!output}>
          Apply to Input
        </Button>
        <Button onClick={() => setInput('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default TextCleanerTool;
